import { useCallback, useEffect } from "react";

import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import { useDrawerFilters } from "../../../drawer-filters";
import { CustomFilters } from "../components/ProviderFilters/CustomFilters";
import { STRINGS } from "../config/strings";
import { formatTime } from "../helper/functions";
import { useProviderSearchStore } from "../store/providerSearchStore";

export function useProviderSearch() {
  const {
    initAddress,
    address,
    providerName,
    providerType,
    providerTypeMatchAll,
    dayOfAvailability,
    timeOfAvailability,
    setData,
    setLoading: setProviderLoading,
    setResultCount,
    resLimit,
    currentPage,
    inNetwork,
    setInNetwork,
    showCustomSearch,
    customAddressSearchText,
    caseId,
    setMaxDistance,
    resetCurrentPage,
  } = useProviderSearchStore();
  const { filterState, activeFilters } = useDrawerFilters(CustomFilters, {
    replace: false,
  });

  const providerRepository = useService("repositories/provider-repository");

  const calculateOffset = () => {
    const offset = (currentPage - 1) * resLimit;
    return offset;
  };

  const fetchProviders = useCallback(async () => {
    try {
      setProviderLoading(true);

      const queryParams = {};

      for (const filter of activeFilters) {
        if (filter?.fieldName && filter?.currentValue !== undefined) {
          queryParams[filter.fieldName] = filter.currentValue;
        }
      }

      if (queryParams?.time) {
        queryParams["time"] = formatTime(queryParams["time"]);
      }
      if (queryParams?.max_distance === undefined || queryParams?.max_distance === null) {
        queryParams["max_distance"] = STRINGS.DEFAULT_MAX_DISTANCE;
      }

      if (queryParams?.in_network_receiving === "0" || queryParams?.in_network_receiving === 0) {
        delete queryParams["in_network_receiving"];
        setInNetwork(0);
      } else {
        queryParams["in_network_receiving"] = true;
        setInNetwork(1);
      }

      setMaxDistance(queryParams["max_distance"]);

      let latitude, longitude, state, id;
      if (showCustomSearch && customAddressSearchText?.trim()?.length === 0) {
        setData([]);
        return;
      } else if (address && address?.additionalDetails) {
        latitude = address?.additionalDetails?.latitude;
        longitude = address?.additionalDetails?.longitude;
        state = address?.additionalDetails?.state;
        id = address?.additionalDetails?.id;
      } else {
        latitude = initAddress?.additionalDetails?.latitude;
        longitude = initAddress?.additionalDetails?.longitude;
        state = initAddress?.additionalDetails?.state;
        id = initAddress?.additionalDetails?.id;
      }

      if (providerName?.trim()) {
        queryParams.name__icontains = providerName;
      }

      if (resLimit && currentPage) {
        queryParams.limit = resLimit;
        queryParams.offset = calculateOffset();
      }

      if (providerType) {
        const typesArray = Array.isArray(providerType) ? providerType : [providerType];
        const ids = typesArray.map((item) => item.id).filter(Boolean);
        if (ids.length) {
          // A single type makes "match all" and "match any" equivalent, so keep
          // the existing single-value param shape for it.
          let key;
          if (ids.length === 1) {
            key = "provider_type";
          } else if (providerTypeMatchAll) {
            key = "provider_type__all";
          } else {
            key = "provider_type__in";
          }
          queryParams[key] = ids.join(",");
        }
      }
      queryParams.state = state;
      if (caseId) {
        queryParams.case_id = caseId;
      }
      let result;
      if (!id) {
        result = await providerRepository.getProviderMapEntries(latitude, longitude, queryParams);
      } else {
        result = await providerRepository.getProviderMapEntriesByAddress(id, queryParams);
      }

      const count = await providerRepository.getTotalCount();
      setResultCount(count);

      setData(result || []);
    } catch (error) {
      console.error("Error fetching providers:", error);
      return ApiRequestErrorHandler(error);
    } finally {
      setProviderLoading(false);
    }
  }, [
    initAddress,
    address,
    providerName,
    providerType,
    providerTypeMatchAll,
    dayOfAvailability,
    timeOfAvailability,
    providerRepository,
    setData,
    setProviderLoading,
    currentPage,
    inNetwork,
    showCustomSearch,
    caseId,
    filterState,
  ]);

  useEffect(() => {
    if (initAddress || address) {
      fetchProviders();
    }
  }, [initAddress, inNetwork, fetchProviders, address]);

  useEffect(() => {
    resetCurrentPage();
  }, [activeFilters]);

  return {
    fetchProviders,
  };
}
